import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { CalendarClock, FileText, Send, Settings, ShieldAlert, Users } from "lucide-react";
import { Badge } from "@/components/atoms/Badge";
import { Button } from "@/components/atoms/Button";
import { CopyButton } from "@/components/atoms/CopyButton";
import { Skeleton } from "@/components/atoms/Skeleton";
import { Drawer } from "@/components/organisms/Drawer";
import { useResource } from "@/hooks/useResource";
import { useScopeOptions } from "@/hooks/useScopeOptions";
import { campaignsService } from "@/services/campaignsService";
import { contactsService } from "@/services/contactsService";
import { branchesService } from "@/services/branchesService";
import { legalEntitiesService } from "@/services/legalEntitiesService";
import { cn } from "@/lib/cn";
import { getApiErrorMessage } from "@/utils/apiErrors";
import { formatDateAbsolute } from "@/utils/date-helpers";
import type { Campaign, CampaignActivationPreview } from "@/types/campaign";
import { CampaignRunsSection } from "./CampaignRunsSection";
import { CampaignActivationPreviewModal } from "./CampaignActivationPreviewModal";
import { describeCron } from "./campaignCron";
import { useCampaignRuns } from "./campaignRuns";
import {
  CAMPAIGN_STATUS_TONE,
  RUN_STATUS_TONE,
  displayRunStatus,
  cadenceLabel,
  formatInstantInZone,
  nextRunLabel,
  sendModeLabel,
  targetTypeLabel,
} from "./campaignConstants";

interface Props {
  open: boolean;
  campaignId: number | null;
  /** Row already in the list — rendered while the detail fetch is in flight. */
  fallback?: Campaign | null;
  /**
   * Template and property names the list already resolved, keyed by id. The
   * campaign itself carries only ids, so without these the drawer can only show
   * the raw uuid — which is what it falls back to for an id that is missing.
   */
  templateNames?: Record<string, string>;
  propertyNames?: Record<string, string>;
  onClose: () => void;
  onEdit: (campaign: Campaign) => void;
  /** Called after a status change so the list can refresh. */
  onStatusChange?: (campaign: Campaign) => void;
}

/**
 * The drawer's tabs. Each owns a whole pane rather than a card in one long
 * scroll, so a block can spread out — the run history in particular needs the
 * height, and the schedule fields read as a group instead of a fragment.
 */
const TABS = [
  { id: "overview", label: "Overview", icon: FileText },
  { id: "schedule", label: "Schedule", icon: CalendarClock },
  { id: "delivery", label: "Delivery", icon: Send },
  { id: "system", label: "System", icon: Settings },
] as const;

type TabId = (typeof TABS)[number]["id"];

function Field({ label, value, colSpan = 1 }: { label: string; value: React.ReactNode; colSpan?: 1 | 2 }) {
  const isEmpty =
    value === "—" || value === null || value === undefined || (typeof value === "string" && !value.trim());
  return (
    <div className={colSpan === 2 ? "col-span-2" : ""}>
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-bridge-subtle">{label}</div>
      <div
        className={cn(
          "text-sm font-medium leading-relaxed",
          isEmpty ? "text-bridge-subtle/40 font-normal" : "text-bridge-fg",
        )}
      >
        {isEmpty ? "—" : value}
      </div>
    </div>
  );
}

function Section({ title, icon, children }: { title: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="bg-white border border-bridge-border rounded-xl p-5 shadow-card transition-all duration-200 hover:shadow-soft">
      <div className="flex items-center gap-2 border-b border-bridge-border/80 pb-3 mb-4">
        {icon}
        <h3 className="text-xs font-bold uppercase tracking-wider text-bridge-fg">{title}</h3>
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-4">{children}</div>
    </section>
  );
}

export function CampaignDetailDrawer({
  open,
  campaignId,
  fallback,
  onClose,
  onEdit,
  onStatusChange,
  templateNames,
  propertyNames,
}: Props) {
  // Entity and branch names come from the signed-in user's own scope tree, the
  // same source the scope picker reads. It only covers the organisation the
  // topbar is currently on, so anything it misses is fetched by id below.
  const { labels: scopeLabels } = useScopeOptions();
  const [fetchedScopeNames, setFetchedScopeNames] = useState<Record<string, string>>({});
  const [runsTick, setRunsTick] = useState(0);
  const { data, loading, error } = useResource<Campaign | null>(
    () => (campaignId != null ? campaignsService.getOne(campaignId) : Promise.resolve(null)),
    [campaignId, open, runsTick],
  );

  // The detail fetch is the source of truth, but the list row keeps the drawer
  // populated while it loads (and carries it if /{id} is unavailable).
  const campaign = data ?? fallback ?? null;
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusOverride, setStatusOverride] = useState<string | null>(null);
  const [activationPreview, setActivationPreview] = useState<CampaignActivationPreview | null>(null);
  const [activationOpen, setActivationOpen] = useState(false);
  const [deletingContacts, setDeletingContacts] = useState(false);
  useEffect(() => setStatusOverride(null), [campaignId, open]);
  useEffect(() => {
    setActivationOpen(false);
    setActivationPreview(null);
  }, [campaignId, open]);
  const status = statusOverride ?? campaign?.status;
  const statusKey = String(status ?? "").toUpperCase();
  const isActive = statusKey === "ACTIVE";

  const applyActivated = (saved: Campaign) => {
    if (!campaign) return;
    setStatusOverride(saved?.status ? String(saved.status) : "ACTIVE");
    setActivationOpen(false);
    setActivationPreview(null);
    setRunsTick((tick) => tick + 1);
    toast.success("Campaign activated.");
    onStatusChange?.(saved?.id != null ? saved : { ...campaign, status: "ACTIVE" });
  };

  const openActivationPreview = async () => {
    if (campaignId == null || !campaign) return;
    setStatusSaving(true);
    try {
      const preview = await campaignsService.activationPreview(campaignId);
      if ((preview.blocked ?? 0) > 0) {
        setActivationPreview(preview);
        setActivationOpen(true);
        return;
      }
      if ((preview.total ?? 0) <= 0) {
        toast.error("There are no contacts on this campaign's labels yet.");
        return;
      }
      if (preview.schedule_in_past) {
        toast.error(
          "The scheduled send time is in the past. Edit the campaign and pick a new time, then try Activate again.",
        );
        return;
      }
      applyActivated(await campaignsService.activate(campaignId));
    } catch (err) {
      toast.error(getApiErrorMessage(err) ?? "Could not activate the campaign. Please try again.");
    } finally {
      setStatusSaving(false);
    }
  };

  const refreshActivationPreview = async () => {
    if (campaignId == null) return;
    const preview = await campaignsService.activationPreview(campaignId);
    setActivationPreview(preview);
    return preview;
  };

  const confirmActivate = async () => {
    if (campaignId == null || !campaign) return;
    setStatusSaving(true);
    try {
      applyActivated(await campaignsService.activate(campaignId));
    } catch (err) {
      toast.error(getApiErrorMessage(err) ?? "Could not activate the campaign. Please try again.");
      try {
        const preview = await refreshActivationPreview();
        if (preview && preview.blocked > 0) {
          setActivationOpen(true);
        }
      } catch {
        /* keep the last report */
      }
    } finally {
      setStatusSaving(false);
    }
  };

  const deleteSelectedContacts = async (contactIds: string[]) => {
    if (contactIds.length === 0) return;
    const confirmed = window.confirm(
      `Delete ${contactIds.length} contact${contactIds.length === 1 ? "" : "s"} from Atlas? This cannot be undone.`,
    );
    if (!confirmed) return;
    setDeletingContacts(true);
    try {
      await contactsService.deletePermanently(contactIds);
      toast.success(
        `Deleted ${contactIds.length} contact${contactIds.length === 1 ? "" : "s"} from Atlas.`,
      );
      await refreshActivationPreview();
    } catch (err) {
      toast.error(getApiErrorMessage(err) ?? "Could not delete those contacts. Please try again.");
    } finally {
      setDeletingContacts(false);
    }
  };

  const toggleStatus = async () => {
    if (campaignId == null || !campaign) return;
    if (!isActive) {
      await openActivationPreview();
      return;
    }
    setStatusSaving(true);
    try {
      const saved = await campaignsService.updateStatus(campaignId, "INACTIVE");
      setStatusOverride(saved?.status ? String(saved.status) : "INACTIVE");
      toast.success("Campaign deactivated.");
      onStatusChange?.(saved?.id != null ? saved : { ...campaign, status: "INACTIVE" });
    } catch (err) {
      toast.error(getApiErrorMessage(err) ?? "Could not update campaign status. Please try again.");
    } finally {
      setStatusSaving(false);
    }
  };

  const [tab, setTab] = useState<TabId>("overview");
  // A different campaign is a different story — always start it from the top.
  useEffect(() => setTab("overview"), [campaignId, open]);

  // Fetched here rather than inside the runs section so the Delivery tab can
  // flag failures without the user having to open it first.
  const runsState = useCampaignRuns(campaignId, runsTick);
  const failedRuns = runsState.tally.failed;

  const summary = campaign?.delivery_summary ?? null;
  const progress = useMemo(() => {
    if (!summary) return null;
    const targeted = summary.targeted_count ?? 0;
    const sent = summary.sent_count ?? 0;
    return { targeted, sent, pct: targeted > 0 ? Math.round((sent / targeted) * 100) : 0 };
  }, [summary]);

  const legalEntityId = campaign?.legal_entity_id?.trim() || null;
  const branchId = campaign?.branch_id?.trim() || null;

  /**
   * A campaign can sit on an entity or branch outside the scope tree the topbar
   * currently offers — a different organisation, or one the signed-in user
   * cannot pick — and then the tree holds no name for it. Those two ids are
   * looked up directly so System Info reads as names either way.
   */
  useEffect(() => {
    if (!open) return;
    const missing = [
      { id: legalEntityId, kind: "entity" as const },
      { id: branchId, kind: "branch" as const },
    ].filter(({ id }) => id && !scopeLabels[id] && !fetchedScopeNames[id]);
    if (missing.length === 0) return;

    let cancelled = false;
    void Promise.allSettled(
      missing.map(async ({ id, kind }) => {
        const record =
          kind === "entity"
            ? await legalEntitiesService.getOne(id as string)
            : await branchesService.getOne(id as string);
        const entity = record as { displayName?: string | null; tradingName?: string | null; legalName?: string | null };
        const branch = record as { name?: string | null };
        const name =
          kind === "entity"
            ? entity.displayName?.trim() || entity.tradingName?.trim() || entity.legalName?.trim()
            : branch.name?.trim();
        return name ? ([id as string, name] as const) : null;
      }),
    ).then((results) => {
      if (cancelled) return;
      const found = results.flatMap((r) => (r.status === "fulfilled" && r.value ? [r.value] : []));
      if (found.length > 0) setFetchedScopeNames((prev) => ({ ...prev, ...Object.fromEntries(found) }));
    });
    return () => {
      cancelled = true;
    };
    // `fetchedScopeNames` is deliberately absent: it is what this effect writes,
    // and the ids already resolved are re-checked whenever the campaign changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, legalEntityId, branchId, scopeLabels]);

  const scopeNames = useMemo(
    () => ({ ...scopeLabels, ...fetchedScopeNames }),
    [scopeLabels, fetchedScopeNames],
  );

  /** A name for an id when one is known, and the id itself when it is not. */
  const nameFor = (id: string | number | null | undefined, names?: Record<string, string>) => {
    if (id == null) return "—";
    const key = String(id).trim();
    if (!key) return "—";
    return names?.[key]?.trim() || key;
  };

  return (
    <>
    <Drawer
      open={open}
      onClose={onClose}
      title={campaign?.name ?? "Campaign Details"}
      subtitle={campaign ? (
        <span className="group/copy inline-flex items-center gap-1">
          <span>ID: {campaign.id}</span>
          <CopyButton
            value={campaign.id}
            label="Campaign ID"
            successMessage="Record ID copied to clipboard"
          />
        </span>
      ) : undefined}
      width="lg"
      actions={
        campaign ? (
          <>
            <Button
              variant={isActive ? "danger" : "primary"}
              className="mr-auto"
              loading={statusSaving}
              onClick={() => void toggleStatus()}
            >
              {isActive ? "INACTIVE" : "Activate"}
            </Button>
            <Button variant="secondary" onClick={onClose} disabled={statusSaving}>
              Close
            </Button>
            <Button variant="primary" onClick={() => onEdit(campaign)} disabled={statusSaving}>
              Edit Campaign
            </Button>
          </>
        ) : undefined
      }
    >
      {loading && !campaign && (
        <div className="space-y-6">
          <Skeleton className="h-28 w-full rounded-xl" />
          <Skeleton className="h-32 w-full rounded-xl" />
          <Skeleton className="h-36 w-full rounded-xl" />
        </div>
      )}

      {error && !campaign && (
        <div className="flex items-center gap-3 rounded-xl border border-bridge-danger/20 bg-bridge-danger/10 p-4 text-bridge-danger">
          <ShieldAlert size={20} />
          <span className="text-sm font-medium">Failed to load campaign details. Please try again.</span>
        </div>
      )}

      {campaign && (
        <div className="flex h-full min-h-0 flex-col gap-4 md:flex-row md:gap-6">
          <aside className="flex shrink-0 flex-row gap-1 overflow-x-auto rounded-2xl border border-bridge-border/50 bg-white/80 p-2 shadow-sm md:sticky md:top-0 md:w-48 md:flex-col md:self-start md:overflow-visible">
            {TABS.map(({ id, label, icon: Icon }) => {
              const active = tab === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setTab(id)}
                  aria-current={active}
                  className={cn(
                    "flex items-center gap-2 whitespace-nowrap rounded-xl px-3 py-2 text-left text-sm transition-colors md:px-4",
                    active
                      ? "bg-[#009BF5] font-medium text-white shadow-sm ring-1 ring-[#009BF5]/20"
                      : "text-bridge-muted hover:bg-bridge-surface-hover hover:text-bridge-fg",
                  )}
                >
                  <Icon size={15} className="shrink-0" />
                  {label}
                  {/* Failures are the one thing worth pulling out of a tab the
                      user has not opened yet. */}
                  {id === "delivery" && failedRuns > 0 && (
                    <span
                      className={cn(
                        "ml-auto rounded-full px-1.5 text-[10px] font-bold tabular-nums",
                        active ? "bg-[#ffffff40] text-white" : "bg-bridge-danger/15 text-bridge-danger",
                      )}
                    >
                      {failedRuns}
                    </span>
                  )}
                </button>
              );
            })}
          </aside>

          <div className="min-h-0 flex-1 overflow-y-auto pr-1">
            {tab === "overview" && (
              <div className="space-y-6">
                <Section title="Overview" icon={<FileText size={16} className="text-bridge-accent" />}>
                  <Field
                    label="Status"
                    value={
                      <Badge tone={CAMPAIGN_STATUS_TONE[String(status)] ?? "neutral"}>
                        {status}
                      </Badge>
                    }
                  />
                  <Field label="Channel" value={targetTypeLabel(campaign.target_type)} />
                  <Field label="Send Mode" value={sendModeLabel(campaign.send_mode)} />
                  <Field label="Next Run" value={nextRunLabel({ ...campaign, status: status ?? campaign.status })} />
                  <Field label="Template" value={nameFor(campaign.template_id, templateNames)} colSpan={2} />
                  <Field label="Property" value={nameFor(campaign.property_id, propertyNames)} colSpan={2} />
                </Section>

                <Section title="Labels" icon={<Users size={16} className="text-bridge-accent" />}>
                  <div className="col-span-2 flex flex-wrap gap-1.5">
                    {(campaign.labels ?? []).length > 0 ? (
                      (campaign.labels ?? []).map((label) => (
                        <Badge key={label.id} tone="accent">
                          {label.name}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-sm text-bridge-subtle/40">No labels attached</span>
                    )}
                  </div>
                </Section>
              </div>
            )}

            {tab === "schedule" && (
              <Section title="Schedule" icon={<CalendarClock size={16} className="text-bridge-accent" />}>
                <Field label="Timezone" value={campaign.timezone} />
                <Field label="Next Run" value={nextRunLabel({ ...campaign, status: status ?? campaign.status })} />
                {campaign.send_mode === "RECURRING" ? (
                  <>
                    <Field
                      label="Repeats"
                      value={describeCron(campaign.cadence_cron) || cadenceLabel(campaign.cadence)}
                    />
                    <Field label="Starts" value={formatInstantInZone(campaign.start_at, campaign.timezone)} />
                    <Field label="Ends" value={formatInstantInZone(campaign.end_at, campaign.timezone)} />
                  </>
                ) : (
                  <Field label="Scheduled For" value={formatInstantInZone(campaign.scheduled_at, campaign.timezone)} colSpan={2} />
                )}
              </Section>
            )}

            {tab === "delivery" && (
              <div className="space-y-6">
                <Section title="Latest Delivery" icon={<Send size={16} className="text-bridge-accent" />}>
                  {summary ? (
                    <>
                      <Field
                        label="Run Status"
                        value={
                          summary.status ? (
                            <Badge tone={RUN_STATUS_TONE[displayRunStatus(summary)] ?? "neutral"}>
                              {displayRunStatus(summary)}
                            </Badge>
                          ) : (
                            "—"
                          )
                        }
                      />
                      <Field label="Run ID" value={summary.run_id != null ? String(summary.run_id) : "—"} />
                      <div className="col-span-2">
                        <div className="mb-1 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider text-bridge-subtle">
                          <span>Progress</span>
                          <span className="tabular-nums">
                            {progress?.sent ?? 0} / {progress?.targeted ?? 0}
                          </span>
                        </div>
                        <div className="h-2 w-full overflow-hidden rounded-full bg-bridge-border/60">
                          <div
                            className="h-full rounded-full bg-bridge-accent transition-[width] duration-300"
                            style={{ width: `${Math.min(100, progress?.pct ?? 0)}%` }}
                          />
                        </div>
                      </div>
                      <Field
                        label="Failed"
                        value={summary.failed_count != null ? String(summary.failed_count) : "—"}
                      />
                      <Field
                        label="Skipped"
                        value={summary.skipped_count != null ? String(summary.skipped_count) : "—"}
                      />
                      <Field label="Started" value={formatInstantInZone(summary.started_at, campaign.timezone)} />
                      <Field label="Ended" value={formatInstantInZone(summary.ended_at, campaign.timezone)} />
                      <Field label="Job ID" value={summary.job_id} colSpan={2} />
                    </>
                  ) : (
                    <div className="col-span-2 text-sm text-bridge-subtle/40">This campaign has not run yet.</div>
                  )}
                </Section>

                <CampaignRunsSection
                  campaign={{ ...campaign, status: status ?? campaign.status }}
                  state={runsState}
                />
              </div>
            )}

            {tab === "system" && (
              <Section title="System Info" icon={<Settings size={16} className="text-bridge-accent" />}>
                <Field label="Created" value={formatDateAbsolute(campaign.created_at)} />
                <Field label="Last Updated" value={formatDateAbsolute(campaign.updated_at)} />
                <Field label="Created By" value={campaign.created_by != null ? String(campaign.created_by) : "—"} />
                <Field label="Modified By" value={campaign.modified_by != null ? String(campaign.modified_by) : "—"} />
                <Field label="Campaign ID" value={String(campaign.id)} />
                <Field label="Legal Entity" value={nameFor(campaign.legal_entity_id, scopeNames)} />
                <Field label="Branch" value={nameFor(campaign.branch_id, scopeNames)} colSpan={2} />
              </Section>
            )}
          </div>
        </div>
      )}
    </Drawer>
    <CampaignActivationPreviewModal
      open={activationOpen}
      preview={activationPreview}
      submitting={statusSaving}
      deleting={deletingContacts}
      onCancel={() => {
        setActivationOpen(false);
        setActivationPreview(null);
      }}
      onDelete={(ids) => void deleteSelectedContacts(ids)}
      onActivate={() => void confirmActivate()}
    />
    </>
  );
}
